import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import { a11y } from '~js/components/a11y';

export class SearchPopdown extends HTMLElement {
  constructor() {
    super();
    this.isPopdownInHeader = this.hasAttribute(a.attributes$r.popdownInHeader);
    this.isPopdownInPage = this.hasAttribute(a.attributes$r.popdownInPage);
    this.popdownBody = this.querySelector(s.selectors$H.popdownBody);
    this.popdownClose = this.querySelector(s.selectors$H.popdownClose);
    this.searchFormInner = this.querySelector(s.selectors$H.searchFormInner);
    this.popularSearchesLink = this.querySelectorAll(s.selectors$H.popularSearchesLink);
    this.searchFormWrapper = this.querySelector(s.selectors$H.searchForm)
      ? this.querySelector(s.selectors$H.searchForm)
      : this.querySelector(s.selectors$H.predictiveSearch);
    this.predictiveSearch = this.searchFormWrapper.matches(s.selectors$H.predictiveSearch);
    this.header = document.querySelector(s.selectors$H.header);
    this.headerSection = this.header?.parentNode;
    this.nav = this.header?.querySelector(s.selectors$H.nav);
    this.mobileMenu = this.headerSection?.querySelector(s.selectors$H.mobileMenu);
    this.a11y = a11y;
    this.ensureClosingOnResizeEvent = () => this.ensureClosingOnResize();
    this.popdownTransitionCallbackEvent = (event) => this.popdownTransitionCallback(event);
    this.detailsToggleCallbackEvent = (event) => this.detailsToggleCallback(event);

    if (this.isPopdownInHeader) {
      this.details = this.querySelector(s.selectors$H.details);
      this.popdownToggle = this.querySelector(s.selectors$H.popdownToggle);
    }
  }

  connectedCallback() {
    if (this.isPopdownInHeader) {
      this.searchFormInner.addEventListener('transitionend', this.popdownTransitionCallbackEvent);
      this.details.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
      this.details.addEventListener('toggle', this.detailsToggleCallbackEvent);
      this.popdownClose.addEventListener('click', () => this.close());
      this.popdownToggle.addEventListener('click', (event) => this.onPopdownToggleClick(event));
      this.popdownToggle.setAttribute('role', 'button');
    }

    if (this.isPopdownInPage) {
      this.popdownClose.addEventListener('click', () => this.triggerPopdownClose());
      this.searchFormWrapper.addEventListener('focusout', () => this.onFocusOut());
      this.searchFormWrapper.input.addEventListener('click', (event) => this.triggerPopdownOpen(event));
    }

    this.popularSearchesLink.forEach((element) => {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        const popularSearchText = event.target.textContent;

        this.searchFormWrapper.input.value = popularSearchText;
        this.searchFormWrapper.submit();
      });
    });
  }

  // Prevent the default details toggle and close manually the popdown
  onPopdownToggleClick(event) {
    const isChrome = navigator.userAgent.includes('Chrome');
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isChrome && !isIOS) {
      event.target.closest(s.selectors$H.details).setAttribute('open', '');
    }
    if (event.target.closest(s.selectors$H.details).hasAttribute('open')) {
      event.preventDefault();
      this.close();
    }
  }

  // Use default details toggle to open the search popdown
  detailsToggleCallback(event) {
    if (event.target.hasAttribute('open')) {
      this.open();
    }
  }

  popdownTransitionCallback(event) {
    if (event.target !== this.searchFormInner) return;

    if (!this.details.classList.contains(c.classes$x.open)) {
      this.onClose();
    } else if (event.propertyName === 'transform') {
      // Wait for the 'transform' transition to complete in order to prevent jumping content issues because of the trapFocus
      this.a11y.trapFocus({
        container: this.searchFormInner,
      });
    }
  }

  onBodyClick(event) {
    const isTargetInPopdown = this.contains(event.target);
    const isHeaderMenuOpened = this.header?.classList.contains(c.classes$x.headerMenuOpened);

    if (isHeaderMenuOpened || isTargetInPopdown) return;
    if (!isTargetInPopdown) this.close();
  }

  onFocusOut() {
    if (!this.predictiveSearch) return;

    requestAnimationFrame(() => {
      if (!this.searchFormWrapper.contains(document.activeElement)) {
        this.searchFormWrapper.close();
      }
    });
  }

  triggerPopdownOpen(event) {
    const isSearchPageWithoutTerms = this.closest(`[${a.attributes$r.searchPerformed}="false"]`);
    let isTouch = matchMedia('(pointer:coarse)').matches;
    const viewportMobile = window.innerWidth < theme.sizes.small;
    const shouldOpenPopdownOnTouchDevice = isTouch || viewportMobile;
    const shouldOpenPopdownOnNoTerms = isSearchPageWithoutTerms != null;

    if (viewportMobile && window.Shopify.designMode) {
      isTouch = true;
    }

    if (!this.nav || !this.mobileMenu) return;

    if (shouldOpenPopdownOnTouchDevice || shouldOpenPopdownOnNoTerms) {
      event.preventDefault();

      const isHeaderCompressed = this.header.classList.contains(c.classes$x.headerCompress);
      let popdownToggle = this.mobileMenu.querySelector(s.selectors$H.popdownToggle);

      if (!isTouch) {
        popdownToggle = isHeaderCompressed
          ? this.nav.querySelector(`${s.selectors$H.navItemsCompress} ${s.selectors$H.popdownToggle}`)
          : this.nav.querySelector(`${s.selectors$H.navIcons} ${s.selectors$H.popdownToggle}`);
      }

      setTimeout(() => {
        popdownToggle?.dispatchEvent(new Event('click', { bubbles: true }));
      }, 300);
    }
  }

  open() {
    this.onBodyClickEvent = (event) => this.onBodyClick(event);
    this.searchFormWrapper.input.setAttribute('aria-expanded', true);

    document.body.classList.add(c.classes$x.searchOpened);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.addEventListener('theme:resize', this.ensureClosingOnResizeEvent);
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));

    // Safari opening transition fix
    requestAnimationFrame(() => {
      this.details.classList.add(c.classes$x.open);
    });
  }

  close() {
    this.a11y.removeTrapFocus();
    this.details.classList.remove(c.classes$x.open);
    if (this.predictiveSearch) this.searchFormWrapper.close();
    this.searchFormWrapper.handleFocusableDescendants(true);
  }

  triggerPopdownClose() {
    if (this.predictiveSearch) this.searchFormWrapper.close();

    if (this.searchFormWrapper.popularSearches) {
      requestAnimationFrame(() => document.activeElement.blur());
    }
  }

  onClose() {
    this.details.removeAttribute('open');
    document.dispatchEvent(new CustomEvent('theme:search:close', { bubbles: true }));
    document.body.classList.remove(c.classes$x.searchOpened);
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.removeEventListener('theme:resize', this.ensureClosingOnResizeEvent);
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
  }

  ensureClosingOnResize() {
    // Due to having multiple <search-popdown> elements in `.mobile-menu`, `.nav--default` or `.menu__item--compress` parents,
    // the element can become hidden when the browser is resized
    // `transitionend` event is then not fired and the closing methods are not finished properly
    const isElementHiddenFromView = this.offsetParent === null;
    if (!isElementHiddenFromView) return;

    this.onClose();
  }
}